import { FC, useEffect, useMemo, useState } from 'react';

import { Box, Container } from '@mui/material';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { Outlet, useNavigate, useSearchParams } from 'react-router';

import { ExpiredBanner } from '@/components/ExpiredBanner';
import { useExpiredBannerContent } from '@/components/ExpiredBanner/hooks/useExpiredBannerContent';
import FactsheetDialog from '@/components/FactsheetDialog';
import { ImpersonatorBanner } from '@/components/ImpersonatorBanner';
import LinkBar from '@/components/LinkBar';
import { NavigationMenu } from '@/components/NavigationMenu';
import UpgradeToPremiumButton from '@/components/UpgradeToPremium/UpgradeToPremiumButton';
import { VersionUpdateBanner } from '@/components/VersionUpdateBanner';
import { GX_AI_FLAG } from '@/constants/featureFlags';
import AskGXButton from '@/features/AIChat/AskGXButton';
import AskGXDrawer from '@/features/AIChat/AskGXDrawer';
import { useAppSelector } from '@/hooks/redux-hooks';
import useUserDetails from '@/hooks/useUserDetails';
import { FACTSHEET_PARAM_NAME } from '@/pages/Facthsheets/constants';

/**
 * Main application layout with navigation, banners, and content area.
 *
 * Features:
 * - Persistent navigation menu (sidebar)
 * - Version update banner
 * - Expired subscription banner
 * - Upgrade to premium button (for basic users)
 * - AI chat integration (feature-flagged)
 * - Factsheet dialog handling
 * - Terms acceptance check and redirect
 * - Responsive container with max-width constraints
 *
 * This layout wraps all authenticated application pages.
 */
export const AppLayout: FC = () => {
  const navigate = useNavigate();
  const tripTokenState = useAppSelector(state => state.tripToken);
  const { isUserBasic, isUserExpired } = useUserDetails();
  const expiredBannerContent = useExpiredBannerContent();
  const showUpgradeButton = useMemo(
    () => isUserBasic && isUserExpired !== undefined && !isUserExpired && !expiredBannerContent,
    [expiredBannerContent, isUserBasic, isUserExpired]
  );
  const showAskGXButton = useFeatureFlagEnabled(GX_AI_FLAG);
  const [searchParams] = useSearchParams();
  const [isAskGXDrawerOpen, setIsAskGXDrawerOpen] = useState(() =>
    Boolean(searchParams.get('prompt'))
  );

  const factsheetCode = searchParams.get(FACTSHEET_PARAM_NAME);

  useEffect(() => {
    if (tripTokenState.status === 'fulfilled' && !tripTokenState.terms) navigate('/welcome');
  }, [tripTokenState.status, tripTokenState.terms, navigate]);

  return (
    <Container
      maxWidth='xl'
      sx={theme => ({
        display: 'flex',
        height: '100vh',
        width: '100%',
        pr: '0!important',
        pl: '0!important',
        overflow: 'hidden',
        [theme.breakpoints.up('xl')]: {
          borderLeft: '1px solid #121836'
        }
      })}
    >
      <FactsheetDialog
        code={factsheetCode || ''}
        open={factsheetCode !== null}
      />
      <NavigationMenu />
      <Box
        sx={{
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: '100vh',
          overflowY: 'auto',
          position: 'relative',
          boxSizing: 'border-box'
        }}
      >
        <VersionUpdateBanner />
        <ExpiredBanner />
        <ImpersonatorBanner />
        <Box sx={{ display: 'flex', gap: 3 }}>
          <LinkBar />
          {showUpgradeButton && <UpgradeToPremiumButton type='upgrade' />}
          {showAskGXButton && (
            <>
              <AskGXButton
                onClick={() => {
                  setIsAskGXDrawerOpen(true);
                }}
              />
              <AskGXDrawer
                open={isAskGXDrawerOpen}
                onClose={e => {
                  e.stopPropagation();
                  setIsAskGXDrawerOpen(false);
                }}
              />
            </>
          )}
        </Box>
        <Outlet />
      </Box>
    </Container>
  );
};
